import { Extension } from "@tiptap/react";

import ToastService from "@services/toast.service";
import type { ITooltipComponent } from "./tooltip";
import { copyToClipboard } from "@utils/misc.util";
import { createTooltipPlugin, TooltipLinkPluginKey } from "./tooltip";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    linkTooltip: {
      /**
       * Open the link dialog.
       *
       * @returns true
       */
      openLinkDialog: () => ReturnType;
      /**
       * Close the link dialog.
       *
       * @returns true
       */
      closeLinkDialog: () => ReturnType;
      /**
       * Set the custom link.
       *
       * If the selection is empty, it will insert a new link with the provided attributes.
       * If the selection is not empty, it will add the link mark to the selected text.
       *
       * @param link - The link to set.
       * @param link.href - The link href.
       * @param link.text - The link text.
       * @returns true
       */
      setCustomLink: (link: { href: string; text: string }) => ReturnType;
    };
  }

  interface EditorEvents {
    /**
     * Open the link dialog.
     *
     * @param link - The link to open the dialog with.
     * @param link.href - The link href.
     * @param link.text - The link text.
     */
    openLinkDialog: {
      href: string;
      text: string;
    };
    /**
     * Close the link dialog.
     */
    closeLinkDialog: void;
  }
}

interface LinkTooltipOptions {
  TooltipComponent: ITooltipComponent;
}

export const LinkTooltip = Extension.create<LinkTooltipOptions>({
  name: "linkTooltip",

  addCommands() {
    return {
      openLinkDialog:
        () =>
        ({ state, editor }) => {
          const { from, to, empty } = state.selection;

          let href = "",
            text = "";

          if (empty) {
            const activeLink = editor.isActive("link");
            const node = state.doc.nodeAt(activeLink ? from - 1 : from);
            const link = node?.marks.find((mark) => mark.type.name === "link")?.attrs.href;

            if (link) {
              href = link;
              text = node?.text ?? "";
            }
          } else {
            const node = state.doc.nodeAt(from);

            if (node) {
              const link = node.marks.find((mark) => mark.type.name === "link");
              if (link) {
                href = link.attrs.href;
                text = state.doc.textBetween(from, to, " ");
              } else {
                text = state.doc.textBetween(from, to, " ");
              }
            }
          }

          editor.emit("openLinkDialog", { href, text });

          return true;
        },
      closeLinkDialog:
        () =>
        ({ editor }) => {
          editor.emit("closeLinkDialog");

          return true;
        },
      setCustomLink:
        (attributes) =>
        ({ editor, state, tr }) => {
          const { href, text } = attributes;
          const { from, to } = state.selection;

          const linkMark = editor.schema.marks.link;
          const $from = tr.doc.resolve(from);
          const $to = tr.doc.resolve(to);

          tr.removeMark(from, to, linkMark);

          const marks = $from.marksAcross($to) || [];
          const newMarks = marks.filter((mark) => mark.type.name !== "link");

          newMarks.push(linkMark.create({ href }));

          tr.replaceRangeWith(from, to, editor.schema.text(text, newMarks));

          editor.chain().closeLinkDialog().focus().run();

          return true;
        },
    };
  },
  onTransaction({ transaction }) {
    const meta = transaction.getMeta(TooltipLinkPluginKey);
    if (!meta) return;

    const { action, anchor, pos } = meta as {
      action: string;
      anchor: HTMLAnchorElement;
      pos: number;
    };

    if (action === "edit") {
      this.editor.chain().setNodeSelection(pos).extendMarkRange("link").openLinkDialog().run();
      return;
    }

    if (action === "copy") {
      void copyToClipboard(anchor.href);
      ToastService.showSuccess("Link copied");
      return;
    }

    if (action === "delete") {
      this.editor.chain().setNodeSelection(pos).extendMarkRange("link").unsetMark("link").run();
      return;
    }
  },
  addProseMirrorPlugins() {
    return [createTooltipPlugin({ TooltipComponent: this.options.TooltipComponent })];
  },
});
